
import { computed, defineComponent, type PropType } from 'vue';
import AppButton from '@/components/Button.vue';
import AppImageHolder from '@/components/ImageHolder.vue';
import { getPagination } from '@/components/table/pagination';
import {
    normalizeTableColumns,
    resolveTableActions,
    TableAction,
    type TableColumn,
    type TableColumnDefinition,
    TableColumnType,
    type TableRow
} from './types';
import { booleanOption, formatDate, renderOption } from '@/common/utils/helpers';
import { RouterLink } from 'vue-router';

export default defineComponent({
    name: 'AppTable',
    components: { AppButton, AppImageHolder, RouterLink },
    props: {
        columns: { type: Array as PropType<TableColumnDefinition<any>[]>, required: true },
        rows: { type: Array as PropType<TableRow[]>, required: true },
        offset: { type: Number, default: 0 },
        limit: { type: Number, default: Number.MAX_SAFE_INTEGER },
        total: { type: Number, required: true },
        actions: { type: Array as PropType<TableAction[]>, default: new Array<TableAction>() },
        isSlotMode: { type: Boolean, default: false }
    },
    emits: ['refresh-data', 'row-clicked'],
    setup(props, { emit, slots }) {
        const currentPage = computed(() => (props.limit > 0 ? Math.floor(props.offset / props.limit) + 1 : 1));
        const maxPage = computed(() => (props.limit > 0 ? Math.ceil(props.total / props.limit) : 0));
        const start = computed(() => (props.total > 0 ? props.offset + 1 : 0));
        const end = computed(() => (props.limit > 0 ? Math.min(props.offset + props.limit, props.total) : 0));

        const normalizedColumns = computed<TableColumn[]>(() =>
            normalizeTableColumns(props.columns, props.isSlotMode, field => Boolean(slots[field]))
        );

        const pagination = computed(() =>
            maxPage.value <= 1 ? [] : getPagination(props.total, props.limit, currentPage.value)
        );

        const paginationSummary = computed(() => `Showing ${start.value} to ${end.value} of ${props.total} entries.`);
        const previousPageClass = computed(() => ({ disabled: currentPage.value === 1 }));
        const nextPageClass = computed(() => ({ disabled: currentPage.value === maxPage.value }));
        const getPageClass = (page: number | string) => ({ active: page === currentPage.value });
        const getCellValue = (row: TableRow, key: string): unknown => (row as Record<string, unknown>)[key];

        const formatDateValue = (value: unknown): string | null => {
            if (typeof value === 'number' || value instanceof Date) {
                return formatDate(value);
            }

            return null;
        };

        const getColumnStyles = (column: TableColumn, row: TableRow) =>
            typeof column.columnStyles === 'function' ? column.columnStyles(row) : column.columnStyles;

        const emitRowClicked = (row: TableRow): void => {
            emit('row-clicked', row);
        };

        const movePage = (page: number): void => {
            if (props.limit <= 0 || page < 1 || page > maxPage.value) return;
            emit('refresh-data', (page - 1) * props.limit);
        };

        return {
            normalizedColumns,
            currentPage,
            maxPage,
            pagination,
            paginationSummary,
            previousPageClass,
            nextPageClass,
            getPageClass,
            getColumnStyles,
            getCellValue,
            formatDateValue,
            renderOption,
            booleanOption,
            emitRowClicked,
            movePage,
            resolveTableActions,
            TableColumnType
        };
    }
});
